import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Card,
  TextInput,
  NumberInput,
  Switch,
  Button,
  Stack,
  Group,
  Text,
  Divider,
  Loader,
  Alert,
  Grid,
  Badge,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconSettings, IconTruck, IconReceipt, IconBuilding, IconCheck, IconAlertCircle, IconSitemap, IconDownload, IconRobot } from '@tabler/icons-react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/admin/AdminLayout';
import { auth } from '@/lib/firebase';

interface ShippingOption {
  id: string;
  name: string;
  price: number;
  enabled: boolean;
}

interface ShopSettings {
  shipping: {
    inpost: ShippingOption;
    courier: ShippingOption;
    freeShippingThreshold: number | null;
  };
  vatRate: number;
  store: {
    name: string;
    email: string;
    phone: string;
    address: string;
    nip: string;
  };
  updatedAt?: any;
  updatedBy?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth?.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

async function fetchSettings(): Promise<ShopSettings> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/admin/settings`, { headers });
  
  if (!response.ok) {
    throw new Error('Failed to fetch settings');
  }
  
  const data = await response.json();
  return data.settings;
}

async function updateSettings(updates: Partial<ShopSettings>): Promise<ShopSettings> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/admin/settings`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(updates),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to update settings');
  }
  
  const data = await response.json();
  return data.settings;
}

export default function AdminSettingsPage() {
  const { user, isAdmin: isAdminUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freeShippingEnabled, setFreeShippingEnabled] = useState(true);
  const [downloadingSitemap, setDownloadingSitemap] = useState(false);
  const [downloadingRobots, setDownloadingRobots] = useState(false);

  // Download sitemap.xml
  const handleDownloadSitemap = async () => {
    try {
      setDownloadingSitemap(true);
      const response = await fetch(`${API_BASE_URL}/api/sitemap.xml`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch sitemap');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sitemap.xml';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      notifications.show({
        title: 'Sitemap downloaded',
        message: 'Place the file in your frontend/public folder',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to download sitemap',
        color: 'red',
      });
    } finally {
      setDownloadingSitemap(false);
    }
  };

  // Download robots.txt
  const handleDownloadRobots = async () => {
    try {
      setDownloadingRobots(true);
      const response = await fetch(`${API_BASE_URL}/api/robots.txt`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch robots.txt');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'robots.txt';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      notifications.show({
        title: 'robots.txt downloaded',
        message: 'Place the file in your frontend/public folder',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to download robots.txt',
        color: 'red',
      });
    } finally {
      setDownloadingRobots(false);
    }
  };

  const form = useForm({
    initialValues: {
      // Shipping
      inpostPrice: 16,
      inpostEnabled: true,
      courierPrice: 20,
      courierEnabled: true,
      freeShippingThreshold: 500,
      // VAT
      vatRate: 23,
      // Store info
      storeName: 'Luxelo',
      storeEmail: '',
      storePhone: '',
      storeAddress: '',
      storeNip: '',
    },
    validate: {
      inpostPrice: (value) => (value < 0 ? 'Price must be non-negative' : null),
      courierPrice: (value) => (value < 0 ? 'Price must be non-negative' : null),
      freeShippingThreshold: (value) => (value < 0 ? 'Threshold must be non-negative' : null),
      vatRate: (value) => (value < 0 || value > 100 ? 'VAT rate must be between 0 and 100' : null),
      storeEmail: (value) => (value && !/^\S+@\S+$/.test(value) ? 'Invalid email' : null),
    },
  });

  useEffect(() => {
    if (!authLoading && (!user || !isAdminUser)) {
      navigate('/xyz/login');
      return;
    }

    if (user && isAdminUser) {
      loadSettings();
    }
  }, [user, isAdminUser, authLoading, navigate]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const settings = await fetchSettings();
      
      setFreeShippingEnabled(settings.shipping.freeShippingThreshold !== null);
      
      form.setValues({
        inpostPrice: settings.shipping.inpost.price,
        inpostEnabled: settings.shipping.inpost.enabled,
        courierPrice: settings.shipping.courier.price,
        courierEnabled: settings.shipping.courier.enabled,
        freeShippingThreshold: settings.shipping.freeShippingThreshold ?? 500,
        vatRate: settings.vatRate * 100, // Convert from decimal to percentage
        storeName: settings.store.name,
        storeEmail: settings.store.email,
        storePhone: settings.store.phone,
        storeAddress: settings.store.address,
        storeNip: settings.store.nip,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    try {
      setSaving(true);
      setError(null);
      
      const updates: Partial<ShopSettings> = {
        shipping: {
          inpost: {
            id: 'inpost',
            name: 'Paczkomat InPost',
            price: values.inpostPrice,
            enabled: values.inpostEnabled,
          },
          courier: {
            id: 'courier',
            name: 'Dostawa pod adres',
            price: values.courierPrice,
            enabled: values.courierEnabled,
          },
          freeShippingThreshold: freeShippingEnabled ? values.freeShippingThreshold : null,
        },
        vatRate: values.vatRate / 100, // Convert from percentage to decimal
        store: {
          name: values.storeName,
          email: values.storeEmail,
          phone: values.storePhone,
          address: values.storeAddress,
          nip: values.storeNip,
        },
      };

      await updateSettings(updates);
      
      notifications.show({
        title: 'Settings saved',
        message: 'Shop settings have been updated successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save settings',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <Container size="lg" py="xl">
          <Group justify="center" py="xl">
            <Loader size="lg" />
          </Group>
        </Container>
      </AdminLayout>
    );
  }

  if (!user || !isAdminUser) {
    return null;
  }

  return (
    <AdminLayout>
      <Container size="lg" py="xl">
        <Stack gap="xl">
          <Group justify="space-between" align="center">
            <Group gap="sm">
              <IconSettings size={28} />
              <Title order={1}>Shop Settings</Title>
            </Group>
            <Badge color="blue" variant="light">
              Configuration
            </Badge>
          </Group>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
              {error}
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="lg">
              {/* Shipping Configuration */}
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group gap="sm" mb="md">
                  <IconTruck size={20} color="var(--mantine-color-blue-6)" />
                  <Title order={3}>Shipping Configuration</Title>
                </Group>

                <Grid gutter="xl">
                  {/* InPost */}
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card withBorder padding="md" radius="sm" bg="gray.0">
                      <Group justify="space-between" mb="md">
                        <Text fw={600}>InPost Parcel Locker</Text>
                        <Switch
                          checked={form.values.inpostEnabled}
                          onChange={(e) => form.setFieldValue('inpostEnabled', e.currentTarget.checked)}
                          label={form.values.inpostEnabled ? 'Enabled' : 'Disabled'}
                          labelPosition="left"
                        />
                      </Group>
                      <NumberInput
                        label="Price (PLN)"
                        description="Shipping cost for InPost delivery"
                        placeholder="16.00"
                        min={0}
                        decimalScale={2}
                        fixedDecimalScale
                        step={0.5}
                        disabled={!form.values.inpostEnabled}
                        {...form.getInputProps('inpostPrice')}
                      />
                    </Card>
                  </Grid.Col>

                  {/* Courier */}
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card withBorder padding="md" radius="sm" bg="gray.0">
                      <Group justify="space-between" mb="md">
                        <Text fw={600}>Courier Delivery</Text>
                        <Switch
                          checked={form.values.courierEnabled}
                          onChange={(e) => form.setFieldValue('courierEnabled', e.currentTarget.checked)}
                          label={form.values.courierEnabled ? 'Enabled' : 'Disabled'}
                          labelPosition="left"
                        />
                      </Group>
                      <NumberInput
                        label="Price (PLN)"
                        description="Shipping cost for courier delivery"
                        placeholder="20.00"
                        min={0}
                        decimalScale={2}
                        fixedDecimalScale
                        step={0.5}
                        disabled={!form.values.courierEnabled}
                        {...form.getInputProps('courierPrice')}
                      />
                    </Card>
                  </Grid.Col>
                </Grid>

                <Divider my="lg" label="Free Shipping" labelPosition="center" />

                <Group justify="space-between" align="flex-start">
                  <div style={{ flex: 1 }}>
                    <Group gap="sm" mb="xs">
                      <Switch
                        checked={freeShippingEnabled}
                        onChange={(e) => setFreeShippingEnabled(e.currentTarget.checked)}
                        label="Enable Free Shipping Threshold"
                        labelPosition="right"
                      />
                    </Group>
                    <Text size="sm" c="dimmed" mb="md">
                      When enabled, orders above the threshold will have free shipping
                    </Text>
                  </div>
                  <NumberInput
                    label="Threshold (PLN)"
                    description="Minimum order value for free shipping"
                    placeholder="500"
                    min={0}
                    decimalScale={2}
                    fixedDecimalScale
                    step={10}
                    w={200}
                    disabled={!freeShippingEnabled}
                    {...form.getInputProps('freeShippingThreshold')}
                  />
                </Group>
              </Card>

              {/* Tax Configuration */}
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group gap="sm" mb="md">
                  <IconReceipt size={20} color="var(--mantine-color-green-6)" />
                  <Title order={3}>Tax Configuration</Title>
                </Group>

                <NumberInput
                  label="VAT Rate (%)"
                  description="Value Added Tax rate applied to all products and shipping"
                  placeholder="23"
                  min={0}
                  max={100}
                  decimalScale={1}
                  fixedDecimalScale
                  step={0.5}
                  w={200}
                  {...form.getInputProps('vatRate')}
                />
                <Text size="xs" c="dimmed" mt="xs">
                  Currently: {form.values.vatRate}% (stored as {(form.values.vatRate / 100).toFixed(2)})
                </Text>
              </Card>

              {/* Store Information */}
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group gap="sm" mb="md">
                  <IconBuilding size={20} color="var(--mantine-color-violet-6)" />
                  <Title order={3}>Store Information</Title>
                </Group>

                <Text size="sm" c="dimmed" mb="md">
                  This information is used on invoices and email communications
                </Text>

                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Store Name"
                      placeholder="Luxelo"
                      {...form.getInputProps('storeName')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Tax ID (NIP)"
                      placeholder="1234567890"
                      {...form.getInputProps('storeNip')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Email"
                      placeholder="contact@luxelo.de"
                      type="email"
                      {...form.getInputProps('storeEmail')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Phone"
                      placeholder="+48 123 456 789"
                      {...form.getInputProps('storePhone')}
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <TextInput
                      label="Address"
                      placeholder="ul. Przykładowa 123, 00-000 Warszawa"
                      {...form.getInputProps('storeAddress')}
                    />
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Submit Button */}
              <Group justify="flex-end">
                <Button
                  type="button"
                  variant="light"
                  onClick={loadSettings}
                  disabled={saving}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  loading={saving}
                  leftSection={<IconCheck size={16} />}
                >
                  Save Settings
                </Button>
              </Group>
            </Stack>
          </form>

          {/* SEO & Sitemap - Outside form since it's not part of settings */}
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group gap="sm" mb="md">
              <IconSitemap size={20} color="var(--mantine-color-teal-6)" />
              <Title order={3}>SEO & Sitemap</Title>
            </Group>

            <Text size="sm" c="dimmed" mb="lg">
              Download the sitemap and robots.txt files and place them in your <code>frontend/public</code> folder for search engines to discover your content.
            </Text>

            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card withBorder padding="md" radius="sm" bg="gray.0">
                  <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                      <IconSitemap size={18} />
                      <Text fw={600}>sitemap.xml</Text>
                    </Group>
                    <Badge size="sm" color="green" variant="light">Dynamic</Badge>
                  </Group>
                  <Text size="sm" c="dimmed" mb="md">
                    Contains all products, categories, and pages. Helps Google index your store.
                  </Text>
                  <Button
                    fullWidth
                    variant="light"
                    color="teal"
                    leftSection={<IconDownload size={16} />}
                    loading={downloadingSitemap}
                    onClick={handleDownloadSitemap}
                  >
                    Download Sitemap
                  </Button>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card withBorder padding="md" radius="sm" bg="gray.0">
                  <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                      <IconRobot size={18} />
                      <Text fw={600}>robots.txt</Text>
                    </Group>
                    <Badge size="sm" color="blue" variant="light">Static</Badge>
                  </Group>
                  <Text size="sm" c="dimmed" mb="md">
                    Tells search engines which pages to crawl and where to find the sitemap.
                  </Text>
                  <Button
                    fullWidth
                    variant="light"
                    color="blue"
                    leftSection={<IconDownload size={16} />}
                    loading={downloadingRobots}
                    onClick={handleDownloadRobots}
                  >
                    Download robots.txt
                  </Button>
                </Card>
              </Grid.Col>
            </Grid>

            <Alert color="blue" mt="lg" variant="light">
              <Text size="sm">
                <strong>After downloading:</strong> Place both files in <code>frontend/public/</code> folder and redeploy your frontend. 
                Then submit your sitemap URL to <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">Google Search Console</a>.
              </Text>
            </Alert>
          </Card>
        </Stack>
      </Container>
    </AdminLayout>
  );
}

